import React from "react";
import { appendRecentProductUpdateDismissal } from "../utils/productUpdatesStorage.js";
import {
  getSupabaseSession,
  subscribeToSupabaseAuthChanges,
} from "../lib/supabaseClient.js";
import {
  createInitialUserUiPreferencesState,
  createUserUiPreferencesController,
  USER_UI_PREFERENCES_SAVE_DEBOUNCE_MS,
} from "../lib/userUiPreferencesController.js";
import {
  claimMissingUserUiPreferences,
  fetchUserUiPreferences,
  patchUserUiPreferences,
} from "../lib/userUiPreferencesApi.js";
import {
  mirrorConfirmedUserUiPreferencesToLegacy,
  readLegacyUserUiPreferencesPatch,
} from "../utils/legacyUserUiPreferences.js";
import { applyDispatchColorIntensityCssVariables } from "../utils/dispatchColorIntensity.js";
import { onPreferencesLeave } from "../utils/preferencesDelegation.js";
import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";
import {
  DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT,
  DEFAULT_DISPATCH_REASSIGNMENT_MODE,
  DEFAULT_LAST_CONSOLIDATION_FROM_DATE,
  DEFAULT_LAST_CONSOLIDATION_TO_DATE,
  DEFAULT_LAST_DISPATCH_DATE,
  DEFAULT_OPTIMIZATION_ANIMATION_ENABLED,
  DEFAULT_PROTECT_AFTER_REASSIGNMENT,
  DEFAULT_ROUTE_MAP_GROUP_RANGES_VISIBLE,
  normalizeDismissedProductUpdateIds,
  normalizeDispatchColorIntensityPercent,
  normalizeDispatchReassignmentModePreference,
  normalizeLastConsolidationDatePreference,
  normalizeLastDispatchDatePreference,
  readUserUiPreferencesCache,
  resolveUserUiPreferencesUserId,
  writeUserUiPreferencesCache,
} from "../utils/userUiPreferences.js";

const UserUiPreferencesContext = React.createContext(null);

function useAutomaticSessionUserId(enabled) {
  const [userId, setUserId] = React.useState("");

  React.useEffect(() => {
    if (!enabled) {
      setUserId("");
      return undefined;
    }

    let isMounted = true;
    const applySession = (session) => {
      if (isMounted) {
        setUserId(resolveUserUiPreferencesUserId(session));
      }
    };
    const unsubscribe = subscribeToSupabaseAuthChanges(applySession);

    void getSupabaseSession()
      .then(({ session: nextSession }) => {
        applySession(nextSession);
      })
      .catch(() => {
        applySession(null);
      });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [enabled]);

  return userId;
}

export function UserUiPreferencesProvider({
  children = null,
  claimMissingPreferences = claimMissingUserUiPreferences,
  debounceMs = USER_UI_PREFERENCES_SAVE_DEBOUNCE_MS,
  keepOpenPageDatesLocal = false,
  loadPreferences = fetchUserUiPreferences,
  mirrorConfirmed = mirrorConfirmedUserUiPreferencesToLegacy,
  onError,
  readCache = readUserUiPreferencesCache,
  readLegacy = readLegacyUserUiPreferencesPatch,
  savePreferences = patchUserUiPreferences,
  session,
  targetWindow,
  userId,
  writeCache = writeUserUiPreferencesCache,
}) {
  const hasExplicitIdentity = userId !== undefined || session !== undefined;
  const automaticUserId = useAutomaticSessionUserId(!hasExplicitIdentity);
  const resolvedUserId = userId !== undefined
    ? resolveUserUiPreferencesUserId(userId)
    : session !== undefined
      ? resolveUserUiPreferencesUserId(session)
      : automaticUserId;
  const onErrorRef = React.useRef(onError);
  const controllerRef = React.useRef(null);
  onErrorRef.current = onError;

  const warmState = React.useMemo(() => createInitialUserUiPreferencesState(
    resolvedUserId,
    { readCache, readLegacy, targetWindow },
  ), [readCache, readLegacy, resolvedUserId, targetWindow]);
  const [controllerState, setControllerState] = React.useState(warmState);
  const visibleState = controllerState.userId === resolvedUserId
    ? controllerState
    : warmState;

  React.useEffect(() => {
    const controller = createUserUiPreferencesController({
      claimMissingPreferences,
      debounceMs,
      keepOpenPageDatesLocal,
      loadPreferences,
      mirrorConfirmed,
      onError(error, meta) {
        onErrorRef.current?.(error, meta);
      },
      readCache,
      readLegacy,
      savePreferences,
      targetWindow,
      userId: resolvedUserId,
      writeCache,
    });
    controllerRef.current = controller;
    setControllerState(controller.getSnapshot());
    const unsubscribe = controller.subscribe(setControllerState);
    const unregisterFlush = onPreferencesLeave(() => controller.flush());
    const unregisterSync = subscribeUserUiPreferences("interface", (preferences) => controller.acceptRemotePreferences(preferences));
    void controller.load().catch(() => {});

    return () => {
      unsubscribe();
      unregisterFlush();
      unregisterSync();
      controller.dispose();
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    };
  }, [claimMissingPreferences, debounceMs, keepOpenPageDatesLocal, loadPreferences, mirrorConfirmed, readCache, readLegacy, resolvedUserId, savePreferences, targetWindow, writeCache]);

  const getCurrentController = React.useCallback(() => {
    const controller = controllerRef.current;
    if (!controller || controller.getSnapshot().userId !== resolvedUserId) {
      throw new Error("Les préférences d’interface de cet utilisateur ne sont pas encore prêtes.");
    }
    return controller;
  }, [resolvedUserId]);

  const updatePreferences = React.useCallback((patch, options) => (
    getCurrentController().updatePreferences(patch, options)
  ), [getCurrentController]);

  const setDispatchColorIntensityPercent = React.useCallback((value, options) => (
    updatePreferences({
      dispatch_color_intensity_percent: normalizeDispatchColorIntensityPercent(value),
    }, options)
  ), [updatePreferences]);

  const resetDispatchColorIntensity = React.useCallback((options) => (
    setDispatchColorIntensityPercent(DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT, options)
  ), [setDispatchColorIntensityPercent]);

  const setProtectAfterReassignment = React.useCallback((value, options) => (
    updatePreferences({
      protect_after_reassignment: typeof value === "boolean"
        ? value
        : DEFAULT_PROTECT_AFTER_REASSIGNMENT,
    }, options)
  ), [updatePreferences]);

  const setDispatchReassignmentMode = React.useCallback((value, options) => (
    updatePreferences({
      dispatch_reassignment_mode: normalizeDispatchReassignmentModePreference(
        value,
        DEFAULT_DISPATCH_REASSIGNMENT_MODE,
      ),
    }, options)
  ), [updatePreferences]);

  const setRouteMapGroupRangesVisible = React.useCallback((value, options) => (
    updatePreferences({
      route_map_group_ranges_visible: typeof value === "boolean"
        ? value
        : DEFAULT_ROUTE_MAP_GROUP_RANGES_VISIBLE,
    }, options)
  ), [updatePreferences]);

  const setOptimizationAnimationEnabled = React.useCallback((value, options) => (
    updatePreferences({
      optimization_animation_enabled: typeof value === "boolean"
        ? value
        : DEFAULT_OPTIMIZATION_ANIMATION_ENABLED,
    }, options)
  ), [updatePreferences]);

  const setLastDispatchDate = React.useCallback((value, options) => (
    updatePreferences({
      last_dispatch_date: normalizeLastDispatchDatePreference(
        value,
        DEFAULT_LAST_DISPATCH_DATE,
      ),
    }, options)
  ), [updatePreferences]);

  const setLastConsolidationDateRange = React.useCallback((fromDate, toDate, options) => (
    updatePreferences({
      last_consolidation_from_date: normalizeLastConsolidationDatePreference(
        fromDate,
        DEFAULT_LAST_CONSOLIDATION_FROM_DATE,
      ),
      last_consolidation_to_date: normalizeLastConsolidationDatePreference(
        toDate,
        DEFAULT_LAST_CONSOLIDATION_TO_DATE,
      ),
    }, options)
  ), [updatePreferences]);

  const setSidebarCollapsed = React.useCallback((value, options = { immediate: true }) => {
    const controller = getCurrentController();
    const nextValue = typeof value === "function"
      ? value(controller.getSnapshot().preferences.sidebar_collapsed)
      : value;
    return controller.updatePreferences({ sidebar_collapsed: nextValue }, options);
  }, [getCurrentController]);

  const setDismissedProductUpdateIds = React.useCallback((values, options) => (
    updatePreferences({
      dismissed_product_update_ids: normalizeDismissedProductUpdateIds(values),
    }, options)
  ), [updatePreferences]);

  const dismissProductUpdate = React.useCallback((updateId, options = { immediate: true }) => {
    const normalizedIds = normalizeDismissedProductUpdateIds([updateId].flat());
    if (normalizedIds.length === 0) {
      return Promise.resolve(visibleState);
    }
    const currentPreferences = getCurrentController().getSnapshot().preferences;
    return setDismissedProductUpdateIds(normalizedIds.reduce(appendRecentProductUpdateDismissal,
      currentPreferences.dismissed_product_update_ids), options);
  }, [getCurrentController, setDismissedProductUpdateIds, visibleState]);

  const dispatchColorIntensityPercent = visibleState.preferences.dispatch_color_intensity_percent;
  const protectAfterReassignment = visibleState.preferences.protect_after_reassignment;
  const dispatchReassignmentMode = visibleState.preferences.dispatch_reassignment_mode;
  const routeMapGroupRangesVisible = visibleState.preferences.route_map_group_ranges_visible;
  const optimizationAnimationEnabled = visibleState.preferences.optimization_animation_enabled;
  const lastDispatchDate = visibleState.preferences.last_dispatch_date;
  const lastConsolidationFromDate = visibleState.preferences.last_consolidation_from_date;
  const lastConsolidationToDate = visibleState.preferences.last_consolidation_to_date;
  const sidebarCollapsed = visibleState.preferences.sidebar_collapsed;
  const dismissedProductUpdateIds = visibleState.preferences.dismissed_product_update_ids;

  React.useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    return applyDispatchColorIntensityCssVariables(
      document.body,
      dispatchColorIntensityPercent,
    );
  }, [dispatchColorIntensityPercent]);

  const flush = React.useCallback(() => {
    const controller = controllerRef.current;
    return controller && controller.getSnapshot().userId === resolvedUserId
      ? controller.flush()
      : Promise.resolve(visibleState);
  }, [resolvedUserId, visibleState]);

  const reload = React.useCallback(() => {
    const controller = controllerRef.current;
    return controller && controller.getSnapshot().userId === resolvedUserId
      ? controller.reload()
      : Promise.resolve(visibleState);
  }, [resolvedUserId, visibleState]);

  const contextValue = React.useMemo(() => ({
    ...visibleState,
    dismissedProductUpdateIds,
    dismissProductUpdate,
    dispatchColorIntensityPercent,
    dispatchReassignmentMode,
    flush,
    optimizationAnimationEnabled,
    lastDispatchDate,
    lastConsolidationFromDate,
    lastConsolidationToDate,
    reload,
    protectAfterReassignment,
    routeMapGroupRangesVisible,
    resetDispatchColorIntensity,
    setDispatchColorIntensityPercent,
    setDispatchReassignmentMode,
    setDismissedProductUpdateIds,
    setOptimizationAnimationEnabled,
    setLastDispatchDate,
    setLastConsolidationDateRange,
    setSidebarCollapsed,
    sidebarCollapsed,
    setProtectAfterReassignment,
    setRouteMapGroupRangesVisible,
    updatePreferences,
  }), [
    dismissedProductUpdateIds,
    dismissProductUpdate,
    dispatchColorIntensityPercent,
    dispatchReassignmentMode,
    flush,
    optimizationAnimationEnabled,
    lastDispatchDate,
    lastConsolidationFromDate,
    lastConsolidationToDate,
    reload,
    protectAfterReassignment,
    routeMapGroupRangesVisible,
    resetDispatchColorIntensity,
    setDispatchColorIntensityPercent,
    setDispatchReassignmentMode,
    setDismissedProductUpdateIds,
    setOptimizationAnimationEnabled,
    setLastDispatchDate,
    setLastConsolidationDateRange,
    setSidebarCollapsed,
    sidebarCollapsed,
    setProtectAfterReassignment,
    setRouteMapGroupRangesVisible,
    updatePreferences,
    visibleState,
  ]);

  return React.createElement(
    UserUiPreferencesContext.Provider,
    { value: contextValue },
    children,
  );
}

export function useUserUiPreferences() {
  const context = React.useContext(UserUiPreferencesContext);
  if (!context) {
    throw new Error("useUserUiPreferences doit être utilisé dans UserUiPreferencesProvider.");
  }
  return context;
}

export default useUserUiPreferences;
